import React from "react";
import { RETAINED_REVENUE_MODE, RetainedRevenueChoice, RetainedRevenueMode } from "api/registrations";
import { toMoneyFormat } from "utils";

interface RetainedRevenueFieldsProps {
  value: RetainedRevenueChoice;
  onChange: (value: RetainedRevenueChoice) => void;
  /** What the student paid; undefined when choosing for several students at once (amounts differ per student). */
  paidAmount?: number;
  advanceAmount?: number;
}

/**
 * "Przychód po usunięciu" choice — how much of what a removed student paid stays in the course revenue.
 * Without amounts (bulk removal) the custom amount option is hidden and the server resolves advance/all per student.
 */
export const RetainedRevenueFields = ({ value, onChange, paidAmount, advanceAmount }: RetainedRevenueFieldsProps) => {
  const isBulk = paidAmount === undefined;
  const nothingPaid = !isBulk && paidAmount === 0;

  const options: { mode: RetainedRevenueMode; label: string }[] = [
    { mode: RETAINED_REVENUE_MODE.NONE, label: "Usuń cały przychód (0,00 PLN)" },
  ];
  if (isBulk || (advanceAmount ?? 0) > 0)
    options.push({
      mode: RETAINED_REVENUE_MODE.ADVANCE,
      label: isBulk ? "Zostaw wpłaconą zaliczkę" : `Zostaw zaliczkę (${toMoneyFormat(advanceAmount ?? 0, true)})`,
    });
  if (!nothingPaid)
    options.push({
      mode: RETAINED_REVENUE_MODE.ALL,
      label: isBulk ? "Zostaw całą wpłaconą kwotę" : `Zostaw całość (${toMoneyFormat(paidAmount ?? 0, true)})`,
    });
  if (!isBulk && !nothingPaid) options.push({ mode: RETAINED_REVENUE_MODE.CUSTOM, label: "Inna kwota" });

  return (
    <div className="space-y-2 text-sm">
      <div className="font-semibold text-slate-800 dark:text-slate-100">Przychód po usunięciu</div>
      {!isBulk && (
        <div className="text-slate-500 dark:text-slate-400">
          Wpłacono: <span className="font-semibold">{toMoneyFormat(paidAmount ?? 0, true)}</span>
          {(advanceAmount ?? 0) > 0 && <> (w tym zaliczka {toMoneyFormat(advanceAmount ?? 0, true)})</>}
        </div>
      )}
      {nothingPaid && (
        <div className="text-slate-500 dark:text-slate-400">
          Kursant nic nie wpłacił — brak przychodu do zatrzymania.
        </div>
      )}
      {options.map((option) => (
        <label key={option.mode} className="flex cursor-pointer items-center gap-2">
          <input
            type="radio"
            className="form-radio"
            checked={value.mode === option.mode}
            onChange={() =>
              onChange({
                mode: option.mode,
                amount: option.mode === RETAINED_REVENUE_MODE.CUSTOM ? value.amount : null,
              })
            }
          />
          <span>{option.label}</span>
        </label>
      ))}
      {value.mode === RETAINED_REVENUE_MODE.CUSTOM && (
        <div className="pl-6">
          <input
            type="number"
            min={0}
            max={paidAmount}
            step="0.01"
            className="form-input w-40"
            value={value.amount ?? ""}
            onChange={(e) =>
              onChange({
                mode: RETAINED_REVENUE_MODE.CUSTOM,
                amount: e.target.value === "" ? null : Number(e.target.value),
              })
            }
          />
          <span className="ml-2">PLN</span>
          {!isRetainedRevenueChoiceValid(value, paidAmount) && (
            <div className="mt-1 text-xs text-rose-500">
              Podaj kwotę od 0 do {toMoneyFormat(paidAmount ?? 0, true)}.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const isRetainedRevenueChoiceValid = (choice: RetainedRevenueChoice, paidAmount?: number) => {
  if (choice.mode !== RETAINED_REVENUE_MODE.CUSTOM) return true;
  if (choice.amount === null || choice.amount === undefined || Number.isNaN(choice.amount)) return false;
  return choice.amount >= 0 && (paidAmount === undefined || choice.amount <= paidAmount);
};

/** Pre-selects the option matching an already stored retained amount. */
export const retainedRevenueChoiceFromAmount = (
  retained: number,
  paidAmount: number,
  advanceAmount: number,
): RetainedRevenueChoice => {
  if (!retained) return { mode: RETAINED_REVENUE_MODE.NONE };
  if (advanceAmount > 0 && retained === advanceAmount) return { mode: RETAINED_REVENUE_MODE.ADVANCE };
  if (retained === paidAmount) return { mode: RETAINED_REVENUE_MODE.ALL };
  return { mode: RETAINED_REVENUE_MODE.CUSTOM, amount: retained };
};
